import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  HStack,
  Icon,
  Text,
  Tooltip,
  VStack,
} from '@elkaso-app/web-design';
import { ICrmDedDetailLicense } from 'apis/crm-ded-details';
import React from 'react';
import { FiAlertTriangle, FiClock } from 'react-icons/fi';

interface LicensesCellProps {
  licenses: ICrmDedDetailLicense[];
  businessName: string;
}

export const LicensesCell: React.FC<LicensesCellProps> = ({ licenses, businessName: _businessName }) => {
  const getStatusColor = (licenseStatus?: string) => {
    if (!licenseStatus) return 'gray';
    if (licenseStatus.toLowerCase().includes('active')) return 'green';
    if (licenseStatus.toLowerCase().includes('inactive')) return 'red';
    if (licenseStatus.toLowerCase().includes('expired')) return 'red';
    if (licenseStatus.toLowerCase().includes('suspended')) return 'yellow';
    return 'gray';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Sort licenses: active first, then by expiry date
  const sortedLicenses = [...licenses].sort((a, b) => {
    // Active licenses first
    const aIsActive = a.licenseStatus?.toLowerCase().includes('active');
    const bIsActive = b.licenseStatus?.toLowerCase().includes('active');

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // Then by expiry date (soonest first)
    if (a.expiryDate && b.expiryDate) {
      return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
    }

    return 0;
  });

  const primaryLicense = sortedLicenses[0];
  const additionalLicenses = sortedLicenses.slice(1);

  if (licenses.length === 0) {
    return (
      <div style={{ height: '100px', display: 'flex', alignItems: 'center', padding: '12px 0' }}>
        <Text color='gray.500' fontStyle='italic'>
          No licenses available
        </Text>
      </div>
    );
  }

  return (
    <div style={{ height: '100px', display: 'flex', alignItems: 'center', padding: '12px 0', maxWidth: '100%' }}>
      <VStack align='start' spacing={2} maxW='100%' w='100%'>
        {/* Primary License */}
        <VStack align='start' spacing={1} maxW='100%'>
          <HStack spacing={2} maxW='100%'>
            <Tooltip label={primaryLicense.licenseNumber}>
              <Text
                fontWeight='semibold'
                fontSize='sm'
                color='gray.800'
                lineHeight='1.2'
                maxW='150px'
                wordBreak='break-word'
                whiteSpace='nowrap'
                overflow='hidden'
                textOverflow='ellipsis'>
                {primaryLicense.licenseNumber}
              </Text>
            </Tooltip>

            <Badge colorScheme={getStatusColor(primaryLicense.licenseStatus)} variant='subtle' size='sm'>
              {primaryLicense.licenseStatus || 'Unknown'}
            </Badge>

            {primaryLicense.isExpiringSoon && (
              <Tooltip label='Expires soon'>
                <Icon as={FiAlertTriangle} color='orange.500' boxSize='14px' />
              </Tooltip>
            )}
          </HStack>

          {primaryLicense.expiryDate && (
            <HStack spacing={1}>
              <Icon as={FiClock} color='gray.500' boxSize='12px' />
              <Text fontSize='xs' color='gray.600'>
                Expires: {formatDate(primaryLicense.expiryDate)}
              </Text>
            </HStack>
          )}
        </VStack>

        {/* Additional Licenses */}
        {additionalLicenses.length > 0 && (
          <Box w='100%'>
            <Accordion allowToggle size='sm'>
              <AccordionItem border='none'>
                <AccordionButton p={1} _hover={{ bg: 'gray.50' }}>
                  <Text fontSize='xs' color='blue.600' fontWeight='medium'>
                    +{additionalLicenses.length} more license{additionalLicenses.length !== 1 ? 's' : ''}
                  </Text>
                  <AccordionIcon ml={1} />
                </AccordionButton>
                <AccordionPanel p={2} bg='gray.50' borderRadius='md'>
                  <VStack spacing={2} align='start'>
                    {additionalLicenses.slice(0, 3).map((license) => (
                      <VStack key={license.id} align='start' spacing={1} w='100%'>
                        <HStack spacing={2} justify='space-between' w='100%'>
                          <Text fontSize='xs' fontWeight='medium' color='gray.700'>
                            {license.licenseNumber}
                          </Text>
                          <Badge colorScheme={getStatusColor(license.licenseStatus)} variant='outline' size='xs'>
                            {license.licenseStatus || 'Unknown'}
                          </Badge>
                        </HStack>
                        {license.expiryDate && (
                          <Text fontSize='xs' color='gray.500'>
                            Expires: {formatDate(license.expiryDate)}
                          </Text>
                        )}
                      </VStack>
                    ))}
                    {additionalLicenses.length > 3 && (
                      <Text fontSize='xs' color='gray.500' fontStyle='italic'>
                        ... and {additionalLicenses.length - 3} more
                      </Text>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        )}
      </VStack>
    </div>
  );
};
